import { useEffect, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";

export interface EmailValidationUpdate {
  type: "email_validation_update";
  phase:
    | "started"
    | "parsing"
    | "parsed"
    | "running"
    | "progress"
    | "completed"
    | "failed";
  jobId: string;
  nbJobId?: number;
  totalEmails?: number;
  leadCount?: number;
  processed?: number;
  total?: number;
  percentComplete?: number;
  nbStatus?: string;
  summary?: {
    total: number;
    valid: number;
    invalid: number;
    catchall: number;
    unknown: number;
    disposable: number;
  };
}

interface UseAdminSocketOptions {
  onValidationUpdate?: (update: EmailValidationUpdate) => void;
}

export function useAdminSocket({ onValidationUpdate }: UseAdminSocketOptions = {}) {
  const socketRef = useRef<Socket | null>(null);
  const onUpdateRef = useRef(onValidationUpdate);
  onUpdateRef.current = onValidationUpdate;

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) return;

    const apiUrl = import.meta.env.VITE_API_URL ?? "";
    // Strip the /api path suffix so we connect to the root socket server
    const socketUrl = apiUrl.replace(/\/api$/, "");

    const socket = io(socketUrl, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.debug("[AdminSocket] connected", socket.id);
    });

    socket.on("disconnect", (reason) => {
      console.debug("[AdminSocket] disconnected:", reason);
    });

    socket.on("notification", (data: any) => {
      if (data?.type === "email_validation_update") {
        onUpdateRef.current?.(data as EmailValidationUpdate);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const pollJobStatus = useCallback(async (jobId: string) => {
    const token = localStorage.getItem("admin_token");
    const apiUrl = import.meta.env.VITE_API_URL ?? "";
    const res = await fetch(`${apiUrl}/leads/validation-jobs/${jobId}/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.json();
  }, []);

  return { pollJobStatus };
}
